import React, {useContext, useState} from "react";
import {Box, Chip, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TextField, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, ArrowRight01Icon, Copy01Icon, Delete02Icon} from "@hugeicons/core-free-icons";
import {NodeType} from "../../../../../model/ModelData";
import useConfirm from "../../../../../hooks/useConfirm";
import {Context as AppContext} from "../../../../../context/AppContext";
import {menuItemsInfo} from "../editMenuItems";
import {TagSelectionModal} from "./tag-selection-modal";
import {EditMenuHeader, HeaderIconButton} from "./EditMenuHeader";
import {EditMenuRow} from "./EditMenuRow";
import {UI, rgba} from "../../uiTokens";

const LOCKED_TYPES = [NodeType.START, NodeType.START_FORM, NodeType.END_FORM];

/**
 * The node edit panel: header (hide / copy-to-flow / delete), the name field,
 * node tags, and the list of editable sections for this node type.
 */
export const NodeEditPanel = ({
                                  node,
                                  setNode,
                                  onUpdate,
                                  onDuplicate,
                                  value,
                                  setValue,
                                  items,
                                  onRemove,
                                  onClose,
                                  showHideMenuButton,
                                  project,
                                  flow,
                              }) => {
    const {updateProject} = useContext(AppContext);
    const [openTags, setOpenTags] = useState(false);
    const [copyAnchor, setCopyAnchor] = useState(null);
    const [otherFlowsAnchor, setOtherFlowsAnchor] = useState(null);

    const otherFlows = (project?.flows || []).filter(f => f.id !== flow?.id);
    const isLocked = LOCKED_TYPES.includes(node?.type);
    const nodeTags = project?.settings?.tags?.filter(i => i?.nodes?.includes(node?.id));

    const confirmDelete = useConfirm("Are you sure you want to delete this node?", node?.name || "");

    const handleCopyTo = (targetFlowId) => {
        setCopyAnchor(null);
        setOtherFlowsAnchor(null);
        onDuplicate(node?.id, targetFlowId);
        onClose();
    };

    const onDelete = async () => {
        const response = await confirmDelete();
        if (response) {
            onRemove(node?.id);
            onClose();
        }
    };

    const handleDeleteTag = async (uid) => {
        const newProject = {
            ...project,
            settings: {
                ...project.settings,
                tags: project?.settings?.tags?.map(tag => {
                    if (tag?.id === uid && tag?.nodes?.includes(node?.id)) {
                        return {...tag, nodes: tag?.nodes?.filter(i => i !== node?.id)};
                    }
                    return {...tag};
                }),
            },
        };
        await updateProject(newProject);
    };

    return (
        // height: "100%" resolves against EditNodeMenu's fixed-height wrapper;
        // overflow: hidden keeps this container from scrolling — only the tab
        // list below (flex: 1 + overflowY: auto) scrolls.
        <Stack
            direction="column"
            gap={1.5}
            sx={{
                p: 1,
                width: "100%",
                height: "100%",
                overflow: "hidden",
                borderRight: `1px solid ${value ? rgba(UI.accent, 0.14) : "transparent"}`,
            }}
        >
            <TagSelectionModal open={openTags} handleClose={() => setOpenTags(false)} node={node} onUpdate={onUpdate}
                               project={project}/>

            <EditMenuHeader
                title={node?.type?.replaceAll("_", " ")}
                onBack={showHideMenuButton ? onClose : undefined}
            >
                <HeaderIconButton
                    label="Copy node"
                    disabled={isLocked}
                    onClick={(e) => setCopyAnchor(e.currentTarget)}
                >
                    <HugeiconsIcon icon={Copy01Icon} size={17}/>
                </HeaderIconButton>
                <HeaderIconButton label="Delete node" danger disabled={isLocked} onClick={onDelete}>
                    <HugeiconsIcon icon={Delete02Icon} size={17}/>
                </HeaderIconButton>

                <Menu
                    anchorEl={copyAnchor}
                    open={!!copyAnchor}
                    onClose={() => {
                        setCopyAnchor(null);
                        setOtherFlowsAnchor(null);
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: 168,
                                maxHeight: 320,
                                px: 0.5,
                                borderRadius: "12px",
                                border: `1px solid ${rgba(UI.accent, 0.16)}`,
                                boxShadow: UI.shadow,
                            },
                        },
                    }}
                    MenuListProps={{dense: true, sx: {py: 0.5}}}
                >
                    <Typography sx={{px: 1.5, py: 0.5, fontSize: "0.7rem", fontWeight: 600, color: UI.muted}}>
                        Copy to
                    </Typography>

                    <MenuItem onClick={() => handleCopyTo(flow?.id)} sx={copyItemSx}>
                        <ListItemIcon sx={{minWidth: "26px !important", color: UI.accent}}><HugeiconsIcon icon={Copy01Icon} size={15}/></ListItemIcon>
                        <ListItemText primaryTypographyProps={{fontSize: "0.78rem"}}>This flow</ListItemText>
                    </MenuItem>

                    {otherFlows.length > 0 && (
                        <MenuItem
                            onClick={(e) => setOtherFlowsAnchor(e.currentTarget)}
                            sx={{...copyItemSx, justifyContent: "space-between"}}
                        >
                            <Box sx={{display: "flex", alignItems: "center"}}>
                                <ListItemIcon sx={{minWidth: "26px !important", color: UI.accent}}><HugeiconsIcon icon={Copy01Icon} size={15}/></ListItemIcon>
                                <ListItemText primaryTypographyProps={{fontSize: "0.78rem"}}>Other flows</ListItemText>
                            </Box>
                            <HugeiconsIcon icon={ArrowRight01Icon} size={14}/>
                        </MenuItem>
                    )}
                    <Menu
                        anchorEl={otherFlowsAnchor}
                        open={!!otherFlowsAnchor}
                        onClose={() => setOtherFlowsAnchor(null)}
                        anchorOrigin={{vertical: "top", horizontal: "right"}}
                        transformOrigin={{vertical: "top", horizontal: "left"}}
                        slotProps={{
                            paper: {
                                sx: {
                                    px: 0.5,
                                    borderRadius: "12px",
                                    border: `1px solid ${rgba(UI.accent, 0.16)}`,
                                    boxShadow: UI.shadow,
                                },
                            },
                        }}
                    >
                        {otherFlows.map(f => (
                            <MenuItem key={f.id} onClick={() => handleCopyTo(f.id)} sx={copyItemSx}>
                                <ListItemIcon sx={{minWidth: "26px !important", color: UI.accent}}><HugeiconsIcon icon={Copy01Icon} size={15}/></ListItemIcon>
                                <ListItemText primaryTypographyProps={{fontSize: "0.78rem"}}>{f.name}</ListItemText>
                            </MenuItem>
                        ))}
                    </Menu>
                </Menu>
            </EditMenuHeader>

            <TextField
                size="small"
                label="Name"
                value={node?.name || ""}
                onChange={(e) => setNode(prev => ({...prev, name: e.target.value}))}
                onBlur={() => onUpdate(node)}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {borderColor: rgba(UI.accent, 0.2)},
                        "&:hover fieldset": {borderColor: rgba(UI.accent, 0.35)},
                        "&.Mui-focused fieldset": {borderColor: UI.accent},
                    },
                }}
            />

            <Stack direction="row" gap={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                {nodeTags?.map(tag => (
                    <Chip
                        key={tag?.id}
                        label={tag?.name || tag?.id}
                        size="small"
                        onDelete={() => handleDeleteTag(tag?.id)}
                        sx={{
                            fontSize: 11.5,
                            fontWeight: 500,
                            color: UI.ink,
                            backgroundColor: rgba(UI.accent, 0.1),
                            "& .MuiChip-deleteIcon": {color: rgba(UI.ink, 0.5), "&:hover": {color: UI.danger}},
                        }}
                    />
                ))}
                <Chip
                    label="Add Tag"
                    size="small"
                    icon={<HugeiconsIcon icon={Add01Icon} size={15}/>}
                    onClick={() => setOpenTags(true)}
                    variant="outlined"
                    sx={{
                        fontSize: 11.5,
                        fontWeight: 500,
                        color: UI.muted,
                        borderColor: rgba(UI.accent, 0.25),
                        "& .MuiChip-icon": {color: UI.accent},
                        "&:hover": {backgroundColor: rgba(UI.accent, 0.08), color: UI.ink},
                    }}
                />
            </Stack>

            {/* flex: 1 + minHeight: 0 so this list, not the page, scrolls */}
            <Box sx={{flex: 1, overflowY: "auto", minHeight: 0}}>
                {items?.map(item => {
                    if (!node?.data?.use_llm && item === "llm_boost") return null;
                    const info = menuItemsInfo[item] || {};
                    return (
                        <EditMenuRow
                            key={item}
                            primary={info.primary}
                            secondary={info.secondary}
                            active={value === item}
                            onClick={() => setValue(value === item ? "" : item)}
                        />
                    );
                })}
            </Box>
        </Stack>
    );
};

const copyItemSx = {minHeight: 0, py: 0.5, borderRadius: "8px", fontSize: "0.78rem"};
